import { SPECIES, SPECIES_IDS, STARTERS, type SpeciesId } from "./species.ts";
import { typeMod } from "./types.ts";
import type { ItemId } from "./items.ts";
import { hashSeed } from "../engine/rng.ts";

export const TRAINER_IDS = ["tavi", "pella", "brin", "sable", "ilex", "vell", "rook", "mica", "nix", "kade", "frida", "morrow", "quartz", "cinder", "opal", "nami", "vex", "lune", "juno", "ash", "kiri", "hana", "osric",
  "remy1", "milo", "remy2", "lexi", "theo", "remy3", "cass", "kole"] as const;
/** Optional Frontier duels (Lv22-36), one per biome; Cinder is the region boss. */
export const FRONTIER_TRAINERS = ["kade", "frida", "morrow", "quartz", "cinder"] as const;
/** Optional Undernet duels (Lv32-39); Lune is the region boss. */
export const UNDERNET_TRAINERS = ["opal", "nami", "vex", "lune"] as const;
/** Optional Sidechain duels (Lv40-50); Osric is the region boss. */
export const SIDECHAIN_TRAINERS = ["juno", "ash", "kiri", "hana", "osric"] as const;
export const CIRCUIT_IDS = ["rook", "mica", "nix"] as const;
/**
 * The story: the KOL Collective, crypto-style Key Opinion Leaders who shill a memeling to their
 * followers, sell it to them at the top and call them exit liquidity. One lieutenant per region, then
 * Kingmaker Kole at the ruin gate. Remy Repost, the scout next door, follows them, gets dumped on,
 * and turns. Story duels are fought once; they never return as encores.
 */
export const STORY_IDS = ["remy1", "milo", "remy2", "lexi", "theo", "remy3", "cass", "kole"] as const;
export type TrainerId = (typeof TRAINER_IDS)[number];
export type CircuitTrainerId = (typeof CIRCUIT_IDS)[number];
export type StoryTrainerId = (typeof STORY_IDS)[number];
export type EncoreTrainerId = Exclude<TrainerId, CircuitTrainerId | StoryTrainerId>;
/** Everyone but the circuit (which already rotates) and the story comes back for encores once Osric falls. */
export const ENCORE_IDS = TRAINER_IDS.filter((id): id is EncoreTrainerId =>
  !(CIRCUIT_IDS as readonly string[]).includes(id) && !(STORY_IDS as readonly string[]).includes(id));
export const isStoryTrainer = (id: TrainerId): id is StoryTrainerId => (STORY_IDS as readonly string[]).includes(id);
export interface TrainerPartner {
  species: SpeciesId;
  level: number;
  arrival?: string;
  openingMove?: string;
}
export interface TrainerDef {
  id: TrainerId;
  name: string;
  x: number;
  y: number;
  team: readonly [TrainerPartner, ...TrainerPartner[]];
  boss?: boolean;
  prize: number;
  supplies?: { id: ItemId; count: number };
  rematchPrize?: number;
  challenge: string;
  intro: string;
  defeat: string;
  after: string;
  lineup?: string;
  lossAdvice?: string;
  /** Set on an encore (post-game) encounter: the week its team was drawn for. */
  encore?: number;
}

/** Optional route duels. Rewards and completion are awarded only after a surviving win. */
export const TRAINERS: Record<TrainerId, TrainerDef> = {
  tavi: {
    id: "tavi", name: "Ticker Tavi", x: 27, y: 8, prize: 180,
    team: [{ species: "fomoling", level: 5 }, { species: "cloutling", level: 6 }],
    challenge: "Two partners. One very noisy queue. Duel?",
    intro: "The bell rang! Wait — that was my kettle. Still counts. The street heard it.",
    defeat: "You kept your head. I kept the receipts. The kettle still files.",
    after: "A quiet walk beats chasing every bell. Pella mends tins on Chartwood's north path: Lv9-10.",
    lossAdvice: "Both are thin. Hit first and the queue folds. The Stubtown kettle is free.",
  },
  pella: {
    id: "pella", name: "Patchkeeper Pella", x: 13, y: 16, prize: 260,
    supplies: { id: "gloss", count: 2 },
    team: [
      { species: "bagsnail", level: 9, openingMove: "foldGuard",
        arrival: "First stitch: a folded shell. Defense drops loosen the seam." },
      { species: "apeling", level: 10, openingMove: "chestBeat",
        arrival: "Second stitch: a proud drummer. Attack drops soften the beat." },
    ],
    challenge: "Two repairs, Lv9-10. Win: 260 and 2 Gloss Tins. Duel?",
    intro: "A loose seam is a chance to try a different stitch. Hold still. The thread is watching.",
    defeat: "Nicely mended. Two polished tins. The shine stays on.",
    after: "Gloss Tins help recruit weakened wilds. Find Laserpup in Drippond: Signal checks Liquid. Brin waits east.",
    lossAdvice: "B opens free tactics. Lower guard or attack with a ready move. The kettle is just south.",
  },
  brin: {
    id: "brin", name: "Tollkeeper Brin", x: 24, y: 19, prize: 320,
    team: [
      { species: "gasgob", level: 12,
        arrival: "First line item: the chimney. It bills the smoke before it clears." },
      { species: "slippeel", level: 13,
        arrival: "Second line item: the skid. It lands one puddle past the quote." },
    ],
    challenge: "Two partners. Zero hidden fees. Duel?",
    intro: "The road is free. The goblin disagrees. I bill the disagreement.",
    defeat: "Invoice settled. Even the smoke is free. Do not tell the goblin.",
    after: "We now accept patience as payment. The meter can wait. The goblin cannot.",
    lossAdvice: "Signal checks Slippeel. Rest and restock. Pella offers Gloss Tins on Chartwood's north path.",
  },
  sable: {
    id: "sable", name: "Coupon Sable", x: 36, y: 20, prize: 480,
    team: [
      { species: "airdropqueen", level: 18,
        arrival: "First coupon: a cape and a tiny grant. The fine print lands last." },
      { species: "yieldyeti", level: 19,
        arrival: "Second coupon: slow snow. It stacks while you decide." },
    ],
    challenge: "Two coupons, Lv18-19. Fine print included. Duel?",
    intro: "Small windfalls. Enormous parachutes. Fine print in the snow. Read it standing.",
    defeat: "A clean landing. Keep the prize. Lose the coupon book. The print still sits.",
    after: "Yieldyeti keeps snow where the reeds cool. The thaw does the accounting. Do not rush the piles.",
    lossAdvice: "Snow outlasts a short crew. Bring Full Cope and a type the cape does not resist.",
  },
  ilex: {
    id: "ilex", name: "Courier Ilex", x: 37, y: 7, prize: 420,
    supplies: { id: "fullcope", count: 1 },
    team: [
      { species: "slippeel", level: 14, openingMove: "thinPool",
        arrival: "First parcel: Thin Pool lowers defense. Switching clears the drop." },
      { species: "cloutling", level: 15, openingMove: "hypeLoop",
        arrival: "Express parcel: speed rises. Fast wings still need a safe landing." },
      { species: "hopiumite", level: 16, openingMove: "steadyShell",
        arrival: "Last parcel: a padded shell. Defense drops help open guarded foes." },
    ],
    challenge: "Three deliveries. Win: 420, 1 Full Cope and a tea recipe. Ready?",
    intro: "I deliver tomorrow's echoes. Today's were late. Sign here anyway.",
    defeat: "All three signed for. A full flask. Do not lose the lid.",
    after: "Full Cope heals in battle. Clear Tea now sells at Signal Stall; it clears stat drops. Sable waits south.",
    lossAdvice: "Switch to clear stat drops. Guard breaks to defense-lowering moves.",
  },
  vell: {
    id: "vell", name: "Wickwarden Vell", x: 8, y: 19, boss: true, prize: 900,
    team: [
      { species: "cloutking", level: 19, openingMove: "hypeLoop",
        arrival: "First bell: Hype Loop builds speed. Slow the wings." },
      { species: "apemajor", level: 20, openingMove: "chestBeat",
        arrival: "Second bell: the drummer builds power. Watch your crew." },
      { species: "candlewyrm", level: 22, openingMove: "trendTilt",
        arrival: "Last bell: the wick dulls your attack. Fresh crew helps." },
    ],
    challenge: "Canopy trial: three partners, Lv19-22. Rest east first. Ready?",
    intro: "Three bells keep this wood awake. Ring them with me. Softly is still ringing.",
    defeat: "Every bell answered. Chartwood knows your footsteps. Softly still counts.",
    after: "Trial cleared! Rook waits by Yield Yard's south kettle. Stronger wilds now roam the southern beds.",
    lossAdvice: "Three bells need three answers. Rest at the canopy kettle and switch when the wick tilts you.",
  },
  rook: {
    id: "rook", name: "Reply Guy Rook", x: 33, y: 26, prize: 1000, rematchPrize: 240,
    supplies: { id: "fullcope", count: 2 },
    team: [
      { species: "diamondshell", level: 23, openingMove: "foldGuard",
        arrival: "A folded reply. Guard goes up; Signal bites the shell." },
      { species: "fomobeast", level: 24, openingMove: "queueCry",
        arrival: "The queue gets loud. A fresh partner shakes off lost attack." },
      { species: "lawnlord", level: 25, openingMove: "sitLawn",
        arrival: "Last reply: it sits down, points outside, and doubles its guard. Lower it." },
    ],
    challenge: "Circuit 1/3. Three replies, then tea. Win: 1000 and 2 Full Cope.",
    intro: "I wrote a short reply. It has three chapters. No one asked.",
    defeat: "Point taken. I will stop typing now. Probably. Draft saved.",
    after: "First stamp earned. Mica waits on Chartwood's south path. Rest at the kettle before you go.",
    lossAdvice: "Cope checks the shell and the lawn; Flex prints on the queue. Two of them double their guard; lower it.",
  },
  mica: {
    id: "mica", name: "Archivist Mica", x: 11, y: 25, prize: 1250, rematchPrize: 300,
    supplies: { id: "gloss", count: 2 },
    team: [
      { species: "slaylord", level: 25, openingMove: "rimLight",
        arrival: "Filed under: lighting budgets. Rim Light doubles the guard, not the HP." },
      { species: "rugwolf", level: 26, openingMove: "falseBeacon",
        arrival: "Filed under: loose cords. Its beacon lowers defense." },
      { species: "moonlord", level: 27, openingMove: "orbitLance",
        arrival: "Filed under: night sky. A bright finish; check your HP." },
    ],
    challenge: "Circuit 2/3. Three archived posts. Win: 1250 and 2 Gloss Tins.",
    intro: "Nothing is deleted. Some things are just misfiled. Including me.",
    defeat: "A new footnote: you were here, and you held on. Ink still wet.",
    after: "Two stamps earned. Nix waits on Gas Gulch's south path. Restock before the final watch.",
    lossAdvice: "Cope checks the lights and the moon; Flex bites the cords. Lower the rim light; a switch clears lost defense.",
  },
  nix: {
    id: "nix", name: "Night Mod Nix", x: 27, y: 26, boss: true, prize: 1600, rematchPrize: 400,
    supplies: { id: "myth", count: 2 },
    team: [
      { species: "detourant", level: 27, openingMove: "workZone", arrival: "First watch: the lane closes and the vest adds attack. It will not get lighter." },
      { species: "chopperalpha", level: 28, openingMove: "rainFreight", arrival: "Second watch: rain freight. Keep a healthy reserve." },
      { species: "depbaron", level: 30, openingMove: "lottoScratch", arrival: "Last watch: a 2am scratch ticket. Every loser buys attack; a switch will not clear it." },
    ],
    challenge: "Circuit 3/3. The final watch. Win: 1600 and 2 Myth Tins.",
    intro: "The thread sleeps when the last lantern goes out. I am still on shift.",
    defeat: "All quiet. Take these tins. The thread can sleep. You should not.",
    after: "Circuit cleared! Repeat laps begin with Rook in Yield Yard. Tea between stops is encouraged.",
    lossAdvice: "Cope opens the work zone; Flex answers the rotors and the slush. Vest and ticket both build attack.",
  },
  // ---- Frontier ------------------------------------------------------------------------------
  kade: {
    id: "kade", name: "Dunebroker Kade", x: 54, y: 22, prize: 620,
    supplies: { id: "gloss", count: 3 },
    team: [
      { species: "wickwolf", level: 30, arrival: "First lot: the candle grew legs and teeth. It burns hot from the first bid." },
      { species: "shillpion", level: 30, arrival: "Second lot: loud, armored, and very sure of itself." },
      { species: "saguarocket", level: 31, arrival: "Third lot: a cactus that went to the moon and came back prickly." },
      { species: "humpfund", level: 32, openingMove: "diamondGrip", arrival: "Final lot: two humps of savings. Guard goes up first." },
    ],
    challenge: "Four lots, Lv30-32. Win: 620 and 3 Gloss Tins. Duel?",
    intro: "Everything in the dunes has a price. Today it is a duel.",
    defeat: "Sold! To the scout with the better crew. Keep the change.",
    after: "BURN melts HODL. LIQUID drowns BURN. Frida waits in Frostfloor to the north; bring fire.",
    lossAdvice: "LIQUID and SIGNAL cut through BURN. Kettle's just west of me.",
  },
  frida: {
    id: "frida", name: "Coldkey Frida", x: 71, y: 8, prize: 700,
    supplies: { id: "fullcope", count: 1 },
    team: [
      { species: "pengwallet", level: 31, arrival: "First key: a wallet that waddles. It never goes online." },
      { species: "diamondpaws", level: 31, arrival: "Second key: paws of pure diamond. It hits in volleys." },
      { species: "yieldyeti", level: 32, arrival: "Third key: the yeti from the reeds, keeping the yield cold." },
      { species: "glacierbag", level: 33, openingMove: "diamondGrip", arrival: "Cold storage: it hardens its guard, then never sells." },
    ],
    challenge: "Four cold keys, Lv31-33. Win: 700 and a Full Cope flask. Duel?",
    intro: "I have not sold since the first snow. I do not plan to start now.",
    defeat: "You thawed my whole portfolio. Respect.",
    after: "BURN and CHAOS crack HODL; RUG pulls it out from under. The Crystal Cache lies east.",
    lossAdvice: "HODL shrugs off FLEX. Bring BURN, CHAOS or RUG, and lower that guard.",
  },
  morrow: {
    id: "morrow", name: "Bogshill Morrow", x: 64, y: 21, prize: 780,
    supplies: { id: "myth", count: 1 },
    team: [
      { species: "swarmlord", level: 32, arrival: "First pitch: one fly, forty accounts, now a whole swarm." },
      { species: "mireshill", level: 33, openingMove: "honeypot", arrival: "Second pitch: ten percent daily. Your speed is stuck in escrow." },
      { species: "honeybaron", level: 33, arrival: "Third pitch: the toad that owns the honey now." },
      { species: "rugwraith", level: 34, arrival: "Final pitch: the carpet itself. Mind the floor." },
    ],
    challenge: "Four pitches, Lv32-34. Win: 780 and a Myth Tin. Duel?",
    intro: "Step into my bog. The yield is incredible. The exit is... being worked on.",
    defeat: "Fine, fine. Refunds are processing. Forever.",
    after: "COPE and VIRAL call out a RUG. Quartz guards the crystals up north-east.",
    lossAdvice: "RUG fears COPE and VIRAL. Clear Tea removes the honeypot's speed drop.",
  },
  quartz: {
    id: "quartz", name: "Seedkeeper Quartz", x: 78, y: 8, prize: 860,
    supplies: { id: "myth", count: 1 },
    team: [
      { species: "shardking", level: 33, arrival: "First word of the seed: shardking." },
      { species: "vaultmole", level: 34, arrival: "Second word: a mole that keeps everything underground." },
      { species: "prismhound", level: 34, openingMove: "frontRun", arrival: "Third word: it moves before you do." },
      { species: "validrake", level: 35, openingMove: "darkPool", arrival: "Fourth word: it signs its own blocks, and never misses." },
    ],
    challenge: "Four words of the seed, Lv33-35. Win: 860 and a Myth Tin. Duel?",
    intro: "Twelve words keep the cache shut. You will need all of them.",
    defeat: "The seed is safe with you. Mostly.",
    after: "Something from the first block sleeps in the deepest grass here. Cinder rules Magma Mint to the south.",
    lossAdvice: "Priority strikes act first; tank them, then hit back. BURN cracks the crystal.",
  },
  cinder: {
    id: "cinder", name: "Burnmaster Cinder", x: 88, y: 21, boss: true, prize: 1200,
    supplies: { id: "fullcope", count: 2 },
    team: [
      { species: "shillscorp", level: 35, arrival: "First burn: the scorpion that shills the flame." },
      { species: "blazebit", level: 35, arrival: "Second burn: a spark that is almost a fire." },
      { species: "magmabull", level: 36, openingMove: "heatMap", arrival: "Third burn: the bull warms up. Its attack only climbs." },
      { species: "moltwisp", level: 37, arrival: "Fourth burn: a ghost flame. It borrows warmth and keeps it." },
      { species: "infernomint", level: 38, arrival: "Final burn: supply unknown. Heat unlimited." },
    ],
    challenge: "Frontier boss: five burns, Lv35-38. Win: 1200 and 2 Full Cope flasks. Duel?",
    intro: "Every token here gets burned eventually. Today, it is your crew's turn.",
    defeat: "The vents go quiet. You earned the frontier. Wear it.",
    after: "The frontier is yours; legends roam the Cache and the Bog. Below the dunes, a gate leads to the Undernet. Opal digs in its caverns.",
    lossAdvice: "LIQUID and SIGNAL douse BURN. The bull's Heat Map stacks attack: hit it hard early or lower it.",
  },
  // ---- Undernet ------------------------------------------------------------------------------
  opal: {
    id: "opal", name: "Spelunker Opal", x: 8, y: 37, prize: 940,
    supplies: { id: "cleartea", count: 3 },
    team: [
      { species: "shroomancer", level: 37, arrival: "First find: mushrooms that learned to cast. The spores sting." },
      { species: "stuckwing", level: 37, openingMove: "spreadFud", arrival: "Second find: a bat that has been pending for weeks. It spreads the doubt." },
      { species: "geodrake", level: 38, arrival: "Third find: the rock-egg hatched. It only got harder." },
      { species: "asicarab", level: 39, arrival: "Last find: a beetle with a mining chip for a shell. Never turned off." },
    ],
    challenge: "Four finds from the deep, Lv37-39. Win: 940 and 3 Clear Teas. Duel?",
    intro: "Every tunnel down here is somebody's pending transaction. Let's see if yours confirms.",
    defeat: "Confirmed! Six blocks deep. You can keep the teas; FUD is thirsty work.",
    after: "Clear Tea washes out FUD, frost, lag, the lot. Coral Ledger lies south; Nami trades by the reef.",
    lossAdvice: "Conditions stack up: FUD grows every turn. Swap out or drink Clear Tea to reset it.",
  },
  nami: {
    id: "nami", name: "Reefbroker Nami", x: 38, y: 47, prize: 1020,
    supplies: { id: "myth", count: 1 },
    team: [
      { species: "pearlclam", level: 38, openingMove: "pearlShield", arrival: "First position: a clam that guards its pearl before anything else." },
      { species: "crabstack", level: 38, arrival: "Second position: the crab stacked its gains. It still trades sideways." },
      { species: "medusell", level: 39, openingMove: "spreadFud", arrival: "Third position: a jelly full of rumors. Mind the sting." },
      { species: "krillionaire", level: 40, arrival: "Closing position: a shrimp that made it." },
    ],
    challenge: "Four reef positions, Lv38-40. Win: 1020 and a Myth Tin. Duel?",
    intro: "The tide sets the price here. Right now it says you owe me a duel.",
    defeat: "Liquidated, and politely. The reef remembers good traders.",
    after: "SIGNAL beats LIQUID; HODL freezes it solid. Sysop Vex runs the Neon Grid to the north-east.",
    lossAdvice: "LIQUID folds to SIGNAL and HODL. The jelly's FUD grows; do not let it linger.",
  },
  vex: {
    id: "vex", name: "Sysop Vex", x: 84, y: 37, prize: 1120,
    supplies: { id: "fullcope", count: 2 },
    team: [
      { species: "rackmoth", level: 39, openingMove: "packetLoss", arrival: "Process one: a moth that eats your packets. Expect lag." },
      { species: "chatlord", level: 39, arrival: "Process two: a chatbot with a crown. It never stops typing." },
      { species: "asicarab", level: 40, arrival: "Process three: a miner on loan from the caverns. Always on." },
      { species: "glitchhound", level: 40, arrival: "Process four: a hound rendered twice. Both halves bite." },
      { species: "fanstorm", level: 41, openingMove: "gasWar", arrival: "Process five: three fans, one furnace. It opens with a gas war." },
    ],
    challenge: "Five running processes, Lv39-41. Win: 1120 and 2 Full Cope flasks. Duel?",
    intro: "Root access requested. Denied. Unless you win.",
    defeat: "Kernel panic. Fine. You have admin rights to the Grid now.",
    after: "VIRAL malware and BURN heat crash AI. Lune waits on Cloud Chain, south of here, above everything.",
    lossAdvice: "AI shrugs off SIGNAL. Bring VIRAL or BURN, and clear lag with Clear Tea.",
  },
  lune: {
    id: "lune", name: "Skykeeper Lune", x: 88, y: 47, boss: true, prize: 1600,
    supplies: { id: "myth", count: 2 },
    team: [
      { species: "craterling", level: 40, arrival: "First phase: a crater with feelings. It pulls you in." },
      { species: "nimbram", level: 40, openingMove: "moonLullaby", arrival: "Second phase: a storm ram. It sings you to sleep, then charges." },
      { species: "pearlclam", level: 41, arrival: "Third phase: a pearl from the tide pools, shining like a second moon." },
      { species: "rocketitan", level: 41, arrival: "Fourth phase: lift-off. It swings hard from the first turn." },
      { species: "lunawolf", level: 42, arrival: "Full moon: the wolf that howls charts upward." },
    ],
    challenge: "Undernet boss: five phases, Lv40-42. Win: 1600 and 2 Myth Tins. Duel?",
    intro: "Up here the only way is up. Let's see how you handle the landing.",
    defeat: "A soft landing. The moon's ledger has your name in it now.",
    after: "Legends wait in the Grid's subnet and the south clouds. East, over the Sidechain Bridge, Juno works the fields.",
    lossAdvice: "CHAOS and HODL drag MOON down. Clear Tea wakes an AFK partner instantly.",
  },
  // ---- Sidechain -----------------------------------------------------------------------------
  juno: {
    id: "juno", name: "Harvester Juno", x: 136, y: 47, prize: 1250,
    supplies: { id: "myth", count: 1 },
    team: [
      { species: "apemajor", level: 42, arrival: "First crop: an ape that came down from the jungle for the harvest." },
      { species: "nutvault", level: 42, arrival: "Second crop: a squirrel with a winter in its cheeks. It digs in." },
      { species: "crowllateral", level: 43, arrival: "Third crop: the crow that borrowed against the scarecrow." },
      { species: "gourdian", level: 43, arrival: "Last crop: the lantern that guards the patch. It never goes out." },
    ],
    challenge: "Four from the last harvest, Lv42-43. Win: 1250 and a Myth Tin. Duel?",
    intro: "Every halving the reward shrinks and the crops get tougher. Let's see what you reap.",
    defeat: "A clean harvest. Take the tin; the fields will grow back.",
    after: "BURN and VIRAL wither STAKE; COPE lays REKT to rest. Ghostchain lies north of the fields.",
    lossAdvice: "STAKE drinks LIQUID. Bring BURN for the gourds and COPE for the crow.",
  },
  ash: {
    id: "ash", name: "Gravewarden Ash", x: 136, y: 37, prize: 1350,
    supplies: { id: "cleartea", count: 3 },
    team: [
      { species: "cryptkeep", level: 42, arrival: "First grave: the keeper of lost keys. It hardens when it can." },
      { species: "catacomb", level: 43, arrival: "Second grave: a cat that went to zero nine times and bounced every time." },
      { species: "arachnft", level: 43, arrival: "Third grave: a one-of-one on eight legs. The web is the floor." },
      { species: "rektoplasm", level: 44, openingMove: "deathSpiral", arrival: "Last grave: a haunting that went viral. It spirals you to sleep." },
    ],
    challenge: "Four restless graves, Lv42-44. Win: 1350 and 3 Clear Teas. Duel?",
    intro: "Every coin down here went to zero. They still want a word with the living.",
    defeat: "They rest easy tonight. Take the teas; the spirals leave a headache.",
    after: "REKT fears COPE and STAKE. The Pump Jungle climbs north of Bridgeport.",
    lossAdvice: "Death Spiral puts a partner AFK: Clear Tea wakes it. COPE and STAKE hit REKT hard.",
  },
  kiri: {
    id: "kiri", name: "Ranger Kiri", x: 134, y: 22, prize: 1450,
    supplies: { id: "fullcope", count: 2 },
    team: [
      { species: "toucoin", level: 44, arrival: "First trail: a toucan that only talks in coins." },
      { species: "vineconda", level: 44, openingMove: "rootLock", arrival: "Second trail: the vine that squeezes. Roots first, questions later." },
      { species: "slothstake", level: 45, openingMove: "compoundGrowth", arrival: "Third trail: the sloth that never unstaked. It compounds." },
      { species: "beakfolio", level: 46, arrival: "Last trail: the loudest beak in the canopy." },
    ],
    challenge: "Four jungle trails, Lv44-46. Win: 1450 and 2 Full Cope flasks. Duel?",
    intro: "In the Pump Jungle everything goes up. The trick is coming back down alive.",
    defeat: "You made it out of the canopy. Most people only make it up.",
    after: "BURN clears a path through STAKE. The gardens of Sakura Stake bloom north of here.",
    lossAdvice: "Slothstake compounds attack: hit it before it grows. BURN and VIRAL cut STAKE.",
  },
  hana: {
    id: "hana", name: "Gardener Hana", x: 112, y: 8, prize: 1550,
    supplies: { id: "gloss", count: 3 },
    team: [
      { species: "koinfish", level: 45, arrival: "First bloom: a koi still learning to climb." },
      { species: "craneprint", level: 45, arrival: "Second bloom: a crane of fresh print. It folds the wind." },
      { species: "bonsaitoise", level: 46, openingMove: "rootLock", arrival: "Third bloom: three centuries of patience on one shell." },
      { species: "ryukoin", level: 47, arrival: "Last bloom: the koi that climbed the sky." },
    ],
    challenge: "Four blooms from the garden, Lv45-47. Win: 1550 and 3 Gloss Tins. Duel?",
    intro: "A garden is a long position. Every petal was planted years ago. Show me yours.",
    defeat: "A beautiful season. Take the tins; the blossoms fall for everyone.",
    after: "The ruins lie east, but the KOL Collective camps at the gate. Osric's relics are CHAOS, REKT, RUG, HODL and STAKE: bring BURN, COPE and VIRAL.",
    lossAdvice: "Ryukoin fears SIGNAL and HODL; the tortoise fears BURN. Rest at the Blossom Kettle.",
  },
  osric: {
    id: "osric", name: "Curator Osric", x: 136, y: 8, boss: true, prize: 2200,
    supplies: { id: "myth", count: 2 },
    team: [
      { species: "wrappharaoh", level: 45, arrival: "First relic: a ruler of a dead chain. It spreads the rumor." },
      { species: "colosstake", level: 45, arrival: "Second relic: the colossus the forest grew on." },
      { species: "vineconda", level: 46, arrival: "Third relic: the vine that swallowed the old temple." },
      { species: "sphynxfi", level: 47, arrival: "Last relic: the vault's guardian. Its riddles make you lag." },
    ],
    challenge: "Sidechain boss: four relics, Lv45-47. Win: 2200 and 2 Myth Tins. Duel?",
    intro: "These ruins remember every chain that ever fell. Let's see if they remember you.",
    defeat: "Catalogued: one champion of the Sidechain. The vault is yours to explore.",
    after: "Two legends wake at Lv49-50: a blossom stag in Sakura Stake's koi beds and a rekt king in the ruin vault.",
    lossAdvice: "Four relics: pace your heals. COPE and STAKE beat REKT; BURN melts the colossus's roots. Clear Tea ends the riddle's lag.",
  },
  // ---- The story: the KOL Collective and Remy Repost ------------------------------------
  // Remy's last partner is stored as Shibonk's counter; trainerEncounter swaps it for other starters.
  remy1: {
    id: "remy1", name: "Remy Repost", x: 15, y: 8, prize: 250,
    team: [
      { species: "cloutling", level: 6, arrival: "First post: a bird that only chirps when someone is watching." },
      { species: "bogfiend", level: 7, arrival: "Pinned post: the starter that beats yours. Remy checked." },
    ],
    challenge: "Remy Repost wants a duel for the timeline: two partners, Lv6-7. Win: 250. Duel?",
    intro: "Remy Repost here, from next door! I followed the KOL Collective. They say whoever duels loudest gets followed back. Watch this.",
    defeat: "Ratio'd by a scout with zero followers. Deleting the screenshot.",
    after: "Moonboy Milo from the Collective recruits in Yield Yard. When I'm strong enough, I'm joining him. KOLs never lose.",
    lossAdvice: "Remy picked the partner that counters yours. Catch a second type in Drippond, then try again.",
  },
  milo: {
    id: "milo", name: "Moonboy Milo", x: 40, y: 21, prize: 700,
    team: [
      { species: "moonlord", level: 27, arrival: "First shill: a moonlord with a price target." },
      { species: "fomobeast", level: 28, arrival: "Second shill: pure FOMO with legs." },
      { species: "cloutking", level: 29, arrival: "Top signal: all clout, no fundamentals." },
    ],
    challenge: "KOL Collective: three shilled picks, Lv27-29. Win: 700. Duel?",
    intro: "Wen moon? Now moon. I pumped every wild in this yard, and my followers are buying the top from me.",
    defeat: "Okay, okay, not financial advice! Kingmaker Kole is going to see this in the group chat.",
    after: "We shill, the followers buy, we sell to them. Followers are exit liquidity. Lexi runs the Frontier; Kole runs all of us.",
    lossAdvice: "Moon and signal hype runs hot. CHAOS and HODL cool it down. Rest at the Snowmelt Kettle.",
  },
  remy2: {
    id: "remy2", name: "Remy Repost", x: 44, y: 18, prize: 600,
    team: [
      { species: "cloutking", level: 30, arrival: "First post: a bird that went viral once and never recovered." },
      { species: "laserhound", level: 30, arrival: "Second post: a hound that chases every trend." },
      { species: "bogbaron", level: 31, arrival: "Pinned post: still the starter that beats yours." },
    ],
    challenge: "Remy Repost, KOL Collective intern: three partners, Lv30-31. Win: 600. Duel?",
    intro: "Milo gave me a shoutout! Nine likes! I duel for the Collective now, so don't make me look bad.",
    defeat: "...Lexi called me a bag holder in the group chat. Is that bad? That sounds bad.",
    after: "Lexi sells 'alpha' by the vents in Magma Mint. I bought some. It's down ninety percent.",
    lossAdvice: "Remy still counters your starter. Bring a partner that covers its weakness.",
  },
  lexi: {
    id: "lexi", name: "Alpha Leak Lexi", x: 80, y: 22, prize: 800,
    team: [
      { species: "shillscorp", level: 36, arrival: "First alpha: the scorpion that shills its own sting." },
      { species: "swarmlord", level: 36, arrival: "Second alpha: forty bot accounts, one fly." },
      { species: "validrake", level: 37, arrival: "Third alpha: it signs every call before it happens." },
      { species: "saguarocket", level: 37, arrival: "Final alpha: a cactus priced for the moon." },
    ],
    challenge: "KOL Collective: four leaked picks, Lv36-37. Win: 800. Duel?",
    intro: "Psst, want alpha? The next memeling to pump is mine. I told my followers first, so they can buy it from me.",
    defeat: "Leaked by a scout with no followers. Theo will thread this to the whole Undernet.",
    after: "Theo writes threads in Neon Harbor. Every scout down there reads him before they read the grass.",
    lossAdvice: "BURN and RUG picks: LIQUID and COPE answer them. Rest at the Vent Kettle.",
  },
  theo: {
    id: "theo", name: "Thread Guy Theo", x: 52, y: 39, prize: 900,
    team: [
      { species: "yapstorm", level: 40, arrival: "Thread 1/4: a hook that talks over everyone." },
      { species: "chatlord", level: 40, arrival: "Thread 2/4: written by a bot, liked by bots." },
      { species: "rackmoth", level: 41, arrival: "Thread 3/4: it eats your packets while you read." },
      { species: "medusell", level: 41, arrival: "Thread 4/4: a jelly full of FUD. Follow for more." },
    ],
    challenge: "KOL Collective: a four-part thread, Lv40-41. Win: 900. Duel?",
    intro: "1/ I've been in memelings since the first block. 2/ My followers hold my bags. 3/ You're about to be one of them.",
    defeat: "97/97: I was wrong. Deleting the thread. Cass will call you out on the Sidechain.",
    after: "Kole wants the ruins' first block listed on the Meme Market: shill it, pump it, dump it on the followers. Osric is the only one saying no.",
    lossAdvice: "AI and VIRAL posts fold to BURN and HODL. Clear Tea washes out FUD and lag.",
  },
  remy3: {
    id: "remy3", name: "Remy Repost", x: 98, y: 38, prize: 900,
    supplies: { id: "fullcope", count: 2 },
    team: [
      { species: "laserhound", level: 42, arrival: "First post in weeks: the hound, grown up." },
      { species: "cloutking", level: 42, arrival: "Second post: the bird, finally quiet." },
      { species: "apemajor", level: 42, arrival: "Third post: an ape that stopped aping." },
      { species: "bogbaron", level: 43, arrival: "Pinned post: the starter that beats yours. Still. Sorry." },
    ],
    challenge: "Remy Repost, done with the KOLs: four partners, Lv42-43. Win: 900 and 2 Full Cope flasks. Duel?",
    intro: "I unfollowed the Collective. They pumped my picks, sold on me, then called me exit liquidity in a thread. One last duel. No screenshots.",
    defeat: "First win I didn't post. It felt better. Take these; Kole waits at the ruin gate.",
    after: "Cass shouts calls from the jungle, and Kole camps at the ruin gate. I'll warn every scout on the bridge.",
    lossAdvice: "Remy's last partner still counters your starter. Lead with something that covers it.",
  },
  cass: {
    id: "cass", name: "Callout Cass", x: 118, y: 22, prize: 1000,
    team: [
      { species: "slaylord", level: 44, arrival: "First call: short the slay." },
      { species: "crowllateral", level: 44, arrival: "Second call: the crow that borrowed against your scarecrow." },
      { species: "apemajor", level: 45, arrival: "Third call: ape in, then ape out." },
      { species: "beakfolio", level: 45, arrival: "Final call: the loudest beak in the canopy." },
    ],
    challenge: "KOL Collective: four hot calls, Lv44-45. Win: 1000. Duel?",
    intro: "I've called every top. Today I'm calling you. Bearish. My followers are already betting against you.",
    defeat: "Called wrong. Again. Kole won't answer my DMs anymore.",
    after: "Kole stands at the ruin gate with two million followers. Bots, all of them. Beat him and Osric opens the vault.",
    lossAdvice: "REKT and FLEX calls: COPE and STAKE answer, and REKT crushes a FLEX crew. Rest at the Jungle Kettle.",
  },
  kole: {
    id: "kole", name: "Kingmaker Kole", x: 118, y: 8, boss: true, prize: 1500,
    supplies: { id: "myth", count: 1 },
    team: [
      { species: "cloutking", level: 46, arrival: "First pump: pure clout, bought with your likes." },
      { species: "glitchhound", level: 46, arrival: "Second pump: a deepfake of a real memeling." },
      { species: "chatlord", level: 47, arrival: "Third pump: two million bot replies." },
      { species: "saguarocket", level: 47, arrival: "Fourth pump: a cactus launched at the top." },
      { species: "rocketitan", level: 47, arrival: "The dump: it rides the rocket, and you hold the bag." },
    ],
    challenge: "KOL Collective boss: five pumps, Lv46-47. Win: 1500 and a Myth Tin. Duel?",
    intro: "Two million followers, all loyal, all exit liquidity. Hand me the first block and I'll make you famous. Refuse, and I'll make you a bag holder.",
    defeat: "Unfollowed... by everyone. Fine. The Collective is done. Osric can keep his block.",
    after: "No more shills, no more dumps. The followers kept their tins. Osric waits in the vault; he heard everything.",
    lossAdvice: "Kole's picks are MOON, FLEX, AI and VIRAL. CHAOS, HODL and BURN cut through the hype.",
  },
};

interface CircuitLineup {
  name: string;
  intro: string;
  defeat: string;
  lossAdvice: string;
  team: TrainerDef["team"];
}

/**
 * Authored side rosters keep each trainer's level band without copying their first stamp.
 * All twenty-seven slots are distinct species and none repeats that trainer's first-clear
 * team, so the circuit's later laps are where the route's evolved forms get fought — and
 * every one of them is recruitable in a post-trial southern bed.
 */
export const CIRCUIT_LINEUPS: Record<CircuitTrainerId, readonly CircuitLineup[]> = {
  rook: [
    {
      name: "Quick Replies", intro: "One ping, one lock, and one thread with chapters. No one asked.",
      defeat: "You read past the notification and kept walking. Draft saved.",
      lossAdvice: "Chaos answers the ping and the thread; Cope answers the lock. Lower raised guard.",
      team: [
        { species: "pinglord", level: 23, openingMove: "badgePop", arrival: "First reply: Badge Pop builds attack. Pressure it before the next ping." },
        { species: "locklord", level: 24, openingMove: "timeout", arrival: "Thread locked. Timeout slows you; switching clears the drop." },
        { species: "threadmaw", level: 25, openingMove: "tabHoard", arrival: "Last reply: forty tabs as a quilt. Defense drops open the stack." },
      ],
    },
    {
      name: "Slow Thread", intro: "A take on the fence, a queue that never moves, a draft never sent.",
      defeat: "Some replies improve when you leave them alone. Mine is still saving.",
      lossAdvice: "Viral bites every one of them. Lower raised defense; a switch clears lost speed.",
      team: [
        { species: "midbaron", level: 23, openingMove: "stayMid", arrival: "A take on the fence. Stay Mid raises defense; lower the guard." },
        { species: "linebaron", level: 24, openingMove: "holdPlace", arrival: "Second in line: Hold Place costs you speed. A fresh partner clears it." },
        { species: "postwyrm", level: 25, openingMove: "unsent", arrival: "Last in line: Unsent doubles its guard. Defense drops post the reply." },
      ],
    },
    {
      name: "Loud Chorus", intro: "Everyone replied at once. Then nobody did. I brought earplugs.",
      defeat: "You heard the rhythm under the noise. I will sit with that.",
      lossAdvice: "Flex doubles on the first two. Chaos or Viral reaches the quiet one.",
      team: [
        { species: "yapstorm", level: 23, openingMove: "talkOver", arrival: "Everyone talks at once. Talk Over lowers attack; a switch clears it." },
        { species: "crashmaw", level: 24, openingMove: "redThread", arrival: "The thread turns red. Red Thread builds attack; do not let it stack." },
        { species: "ghostwyrm", level: 25, openingMove: "leftOnRead", arrival: "Last word: Left on Read costs you speed. Switching clears the drop." },
      ],
    },
  ],
  mica: [
    {
      name: "Rain Ledger", intro: "Slush receipts, gravy stains, and one very damp decree.",
      defeat: "One dry page remains. I saved it for your name.",
      lossAdvice: "Cope checks the first two; Chaos doubles on the decree. Lower the raised guards.",
      team: [
        { species: "auditlord", level: 25, openingMove: "fileAway", arrival: "First page: filed and guarded. Defense drops reopen the ledger." },
        { species: "gravylord", level: 26, openingMove: "napkinWrap", arrival: "Second page: a napkin guard. Defense drops open the platter." },
        { species: "honkference", level: 27, openingMove: "muteThread", arrival: "Final page: Mute Thread lowers attack. A fresh partner clears it." },
      ],
    },
    {
      name: "Ember Notes", intro: "These margins glow. Do not lick your fingers. Or the wick.",
      defeat: "The book survived. So did a very good story. I tabbed it.",
      lossAdvice: "Cope, then Viral, then Flex. The last two raise guard; lower it before you swing.",
      team: [
        { species: "cookwyrm", level: 25, openingMove: "lowHeat", arrival: "A slow simmer. Low Heat builds attack; press it before it boils." },
        { species: "kilnmutt", level: 26, openingMove: "kettleSit", arrival: "The kiln sits down. Defense drops crack a banked fire." },
        { species: "maldmaw", level: 27, openingMove: "holdIt", arrival: "The last margin seethes behind a lid. Lower the guard, then hit." },
      ],
    },
    {
      name: "Margin Choir", intro: "The footnotes started singing. I gave them a shelf and a curfew.",
      defeat: "Your name fits neatly between the verses. Pencil, not ink.",
      lossAdvice: "Cope checks the first two; Viral reaches the last wish. Clear lost attack.",
      team: [
        { species: "ehvergreen", level: 25, openingMove: "sorryBud", arrival: "The choir apologises first. Sorry Bud lowers attack; a switch clears it." },
        { species: "lorelord", level: 26, openingMove: "blankStare", arrival: "Second verse: Blank Stare lowers your defense. Switching clears it." },
        { species: "delulord", level: 27, openingMove: "plotArmor", arrival: "Last verse writes its own armour. Lower the guard before the wish." },
      ],
    },
  ],
  nix: [
    {
      name: "Quiet Watch", intro: "Snow muffles the bells. Listen for the next step. I already did.",
      defeat: "A quiet shift. You kept every lantern company. Clock out.",
      lossAdvice: "Chaos opens the snow, Flex the fort, Viral the tuque. Clear lost speed early.",
      team: [
        { species: "chillossus", level: 27, openingMove: "touchSnow", arrival: "First watch: Touch Snow costs you speed. A fresh partner clears it." },
        { species: "napocalypse", level: 28, openingMove: "blanketFort", arrival: "Second watch: a fort of blankets. Defense drops get under it." },
        { species: "tuquelord", level: 30, openingMove: "blizzardNod", arrival: "Last watch: the blizzard nods. It hits cold; keep a healthy reserve." },
      ],
    },
    {
      name: "Signal Watch", intro: "A closed gate, a long night feed, and one last quoted word.",
      defeat: "Message received. The ridge can sleep now.",
      lossAdvice: "One Flex partner answers all three. Openers cut attack and speed; switch to clear.",
      team: [
        { species: "gatewyrm", level: 27, openingMove: "guestList", arrival: "First watch: the list closes. Guest List lowers attack; switch to clear." },
        { species: "doomwyrm", level: 28, openingMove: "keepReading", arrival: "Second watch: Keep Reading costs you speed. A fresh partner clears it." },
        { species: "ratioking", level: 30, openingMove: "quotePin", arrival: "Last watch: Quote Pin lowers attack. Keep one healthy reserve." },
      ],
    },
    {
      name: "Crowded Watch", intro: "Nobody went home. The night shift brought a chorus and no chairs.",
      defeat: "Even the loud ones are yawning. Well played. Lights out.",
      lossAdvice: "Cope checks the first two; Flex doubles on the clip. Clear lost attack.",
      team: [
        { species: "vibewyrm", level: 27, openingMove: "nodOff", arrival: "First watch: Nod Off lowers attack. A fresh partner shakes it off." },
        { species: "clapback", level: 28, openingMove: "boardCrash", arrival: "Second watch: the boards take it. It hits hard from the first turn." },
        { species: "cliplord", level: 30, openingMove: "loopClip", arrival: "Last watch: Loop Clip doubles its speed. Slow it or out-hit it." },
      ],
    },
  ],
};

export interface CircuitState {
  seed?: string;
  defeatedTrainers: readonly TrainerId[];
  circuitRematches: number;
  circuitPracticeWins?: Partial<Record<CircuitTrainerId, number>>;
  /** Encore wins: trainer id -> the week it was last beaten. */
  encore?: Partial<Record<TrainerId, number>>;
  /** Current week (weekOf in goals.ts). Without it, encores stay closed. */
  week?: number;
  /** Species caught, first one first: the starter, which Remy counters. */
  caught?: readonly string[];
}

// ---- Encores: the post-game ---------------------------------------------------------------

/** Lines for the week-by-week rematches, in each trainer's own voice. */
const ENCORE_LINES: Record<EncoreTrainerId, { intro: string; defeat: string }> = {
  tavi: { intro: "The bell rang for real this time. I brought the whole queue, all grown up.", defeat: "Rang, answered, closed. Same time next week? The queue will be longer." },
  pella: { intro: "New thread, stronger stitch. Everything I patched since you left can bite now.", defeat: "Torn clean through. I'll re-sew the whole crew by next week." },
  brin: { intro: "The toll went up. Inflation, the goblin says. Pay in duels.", defeat: "Paid in full. The goblin wants a rematch next week. So do I." },
  sable: { intro: "Bigger coupons, bigger parachutes. The fine print now includes you.", defeat: "Expired, and I read every line. New offer next week." },
  ilex: { intro: "I delivered your first win to the whole map. Now I'm delivering a harder one.", defeat: "Signed, sealed, lost. Next week's parcel is heavier." },
  vell: { intro: "The three bells rang on their own last night. They wanted you back.", defeat: "The wood sleeps again. Until next week's bells." },
  kade: { intro: "Prices went up in the dunes. So did my crew.", defeat: "You got a bargain. Next week I'm charging double." },
  frida: { intro: "Still haven't sold. But I did put everything into this crew.", defeat: "Held and lost. I'll hold harder next week." },
  morrow: { intro: "The bog has new yield. Same exit policy.", defeat: "You found the exit. It moves every week, you know." },
  quartz: { intro: "I added a thirteenth word to the seed. It's your name.", defeat: "The cache opens for you again. I'll change the words by next week." },
  cinder: { intro: "The vents were cold without you. Let's burn something worth remembering.", defeat: "Burned down to the wick. I'll relight the forge next week." },
  opal: { intro: "I dug deeper since you left. What I found is on my team.", defeat: "Confirmed on the first block. The tunnels shift again next week." },
  nami: { intro: "The tide turned your way last time. I've been waiting for it to turn back.", defeat: "High tide, your favor. The reef changes its mind every week." },
  vex: { intro: "Root access granted, one more time. I patched every hole you used.", defeat: "Rebooted. I'll push a new build next week." },
  lune: { intro: "The sky got bigger. So did my crew. Mind the landing.", defeat: "A clean landing. Come back up next week, the view changes." },
  juno: { intro: "Another halving, another harvest. This crop fought back all season.", defeat: "You reaped the whole field. It regrows by next week." },
  ash: { intro: "The graves whispered your name all month. Let's give them a reason.", defeat: "Rest easy, all of you. The graves wake again next week." },
  kiri: { intro: "Everything in the jungle kept climbing. So did my crew.", defeat: "Back down alive, and ahead. The canopy grows again next week." },
  hana: { intro: "The garden bloomed out of season. Every petal is for you.", defeat: "A beautiful loss. I'll plant a new bed for next week." },
  osric: { intro: "The ruins remembered you. They asked me to test that memory properly.", defeat: "The ruins will carve your name. Come back next week; I'll bring a new exhibit." },
};

const finalForm = (id: SpeciesId): SpeciesId => {
  let s = id;
  while (SPECIES[s].evolvesTo) s = SPECIES[s].evolvesTo!;
  return s;
};

const bst = (id: SpeciesId) => SPECIES[id].hp + SPECIES[id].atk + SPECIES[id].def + SPECIES[id].spd;

/**
 * This week's encore team, all Lv50: the trainer's old ace, fully evolved, last, after four
 * partners (five for region bosses) drawn fresh each week from the stronger half of the final
 * forms that share the trainer's types. No RNG: a hash of trainer and week picks them.
 * Encore partners mint with their level's moves, so they arrive with coverage and finishers.
 */
export function encoreTeam(id: TrainerId, week: number): TrainerPartner[] {
  const base = TRAINERS[id].team;
  const ace = finalForm(base[base.length - 1]!.species);
  const types = new Set(base.flatMap((p) => SPECIES[finalForm(p.species)].types));
  const pool = SPECIES_IDS.filter((s) => !SPECIES[s].evolvesTo && SPECIES[s].catch > 8 && s !== ace && SPECIES[s].types.some((t) => types.has(t)));
  const strong = [...pool].sort((a, b) => bst(b) - bst(a) || a.localeCompare(b)).slice(0, Math.max(6, Math.ceil(pool.length / 2)));
  const drawn = strong.sort((a, b) => hashSeed(`${id}:${week}:${a}`) - hashSeed(`${id}:${week}:${b}`) || a.localeCompare(b))
    .slice(0, TRAINERS[id].boss ? 5 : 4);
  return [...drawn, ace].map((species) => ({ species, level: 50 }));
}

export function encorePrize(id: TrainerId): number {
  return id === "osric" ? 3500 : TRAINERS[id].boss ? 2500 : 1500;
}

/** Osric is down, this trainer was beaten once, and this week's encore is still unplayed. */
export function encoreOpen(trainer: TrainerDef, state: CircuitState): boolean {
  return encoreStage(trainer, state) === "open";
}

function encoreStage(trainer: TrainerDef, state: CircuitState): "open" | "done" | null {
  if (state.week === undefined || isCircuitTrainer(trainer.id) || isStoryTrainer(trainer.id)) return null;
  if (!state.defeatedTrainers.includes("osric") || !state.defeatedTrainers.includes(trainer.id)) return null;
  return state.encore?.[trainer.id] === state.week ? "done" : "open";
}

export function isCircuitTrainer(id: TrainerId): id is CircuitTrainerId {
  return CIRCUIT_IDS.some(circuitId => circuitId === id);
}

/** The starter whose types hit this one hardest and take the least back: Remy always picks it. */
function counterStarter(mine: SpeciesId): SpeciesId {
  const hit = (a: SpeciesId, b: SpeciesId) => Math.max(...SPECIES[a].types.map((t) => typeMod(t, SPECIES[b].types)));
  return STARTERS.filter((s) => s !== mine).sort((a, b) => (hit(b, mine) - hit(mine, b)) - (hit(a, mine) - hit(mine, a)) || a.localeCompare(b))[0]!;
}
/** A line's form at a level (the same walk the wild and trainer data use). */
function formAt(id: SpeciesId, level: number): SpeciesId {
  let s = id;
  while (SPECIES[s].evolvesTo && SPECIES[s].evolveLevel !== undefined && level >= SPECIES[s].evolveLevel!) s = SPECIES[s].evolvesTo!;
  return s;
}
/** Remy's last partner is the starter that counters the scout's own (read from the first catch). */
function rivalTeam(trainer: TrainerDef, state: CircuitState): TrainerDef {
  const mine = (STARTERS as readonly string[]).includes(state.caught?.[0] ?? "") ? state.caught![0] as SpeciesId : "shibonk";
  const rival = counterStarter(mine);
  const team = trainer.team.map((p, i) => (i === trainer.team.length - 1 ? { ...p, species: formAt(rival, p.level) } : p));
  return { ...trainer, team: team as unknown as TrainerDef["team"] };
}

/** Preview and battle resolve the same roster without drawing from combat RNG. Only wins rotate it. */
export function trainerEncounter(trainer: TrainerDef, state: CircuitState): TrainerDef {
  if (trainer.id === "remy1" || trainer.id === "remy2" || trainer.id === "remy3") return rivalTeam(trainer, state);
  if (encoreStage(trainer, state) === "open") {
    const lines = ENCORE_LINES[trainer.id as EncoreTrainerId];
    const team = encoreTeam(trainer.id, state.week!) as unknown as TrainerDef["team"];
    return { ...trainer, team, intro: lines.intro, defeat: lines.defeat, encore: state.week!, lineup: "Encore",
      lossAdvice: "Level fifty, full kits and coverage. Rest at a kettle, then bring answers for their types." };
  }
  if (!isCircuitTrainer(trainer.id) || !state.defeatedTrainers.includes(trainer.id)) return trainer;
  const practicing = !CIRCUIT_IDS.every(id => state.defeatedTrainers.includes(id));
  const wins = practicing ? state.circuitPracticeWins?.[trainer.id] ?? 0
    : Math.floor(state.circuitRematches / CIRCUIT_IDS.length);
  const variants = CIRCUIT_LINEUPS[trainer.id];
  const offset = hashSeed(`${state.seed ?? "drippond"}:${trainer.id}:lineup`) % variants.length;
  const lineup = variants[(offset + wins) % variants.length]!;
  return { ...trainer, team: lineup.team, intro: lineup.intro, defeat: lineup.defeat,
    lossAdvice: lineup.lossAdvice, lineup: lineup.name };
}

/** A saved rotation gives repeatable training without replaying first-clear gifts. */
export function trainerLock(trainer: TrainerDef, state: CircuitState): string | null {
  const index = CIRCUIT_IDS.findIndex((id) => id === trainer.id);
  const defeated = state.defeatedTrainers;
  if (index < 0) {
    if (trainer.id === "osric" && !defeated.includes("osric") && !defeated.includes("kole")) {
      return "The vault is sealed while the KOL Collective camps at the ruin gate. Beat Kingmaker Kole first.";
    }
    if (!defeated.includes(trainer.id)) return null;
    const stage = encoreStage(trainer, state);
    return stage === "open" ? null : stage === "done" ? "You won this week's encore. A new crew arrives on Monday." : trainer.after;
  }
  if (!defeated.includes("vell")) return "Clear Vell's Lv19-22 trial in Chartwood first. Then meet Rook at Yield Yard's south kettle.";
  if (!defeated.includes(trainer.id)) {
    if (index > 0 && !defeated.includes(CIRCUIT_IDS[index - 1]!)) {
      return `First, earn a stamp from ${TRAINERS[CIRCUIT_IDS[index - 1]!].name} on the south path.`;
    }
    return null;
  }
  if (!CIRCUIT_IDS.every((id) => defeated.includes(id))) return null; // Practice while earning the remaining stamps.
  const next = TRAINERS[CIRCUIT_IDS[state.circuitRematches % CIRCUIT_IDS.length]!];
  return next.id === trainer.id ? null : `Next lap stop: ${next.name}. Follow Rook, Mica, then Nix; rest between duels.`;
}

export function trainerChallenge(trainer: TrainerDef, state: CircuitState): string {
  const encounter = trainerEncounter(trainer, state);
  if (encounter.encore !== undefined) return `Encore: ${encounter.team.length} partners at Lv50, a new crew every week. Win: ${encorePrize(trainer.id)}. Duel?`;
  const next = CIRCUIT_IDS.find(id => !state.defeatedTrainers.includes(id));
  if (state.defeatedTrainers.includes(trainer.id) && trainer.rematchPrize && next) {
    return `${encounter.lineup}. Practice: ${trainer.rematchPrize}. Next: ${TRAINERS[next].name}. Ready?`;
  }
  return state.defeatedTrainers.includes(trainer.id) && trainer.rematchPrize
    ? `Lap ${Math.floor(state.circuitRematches / CIRCUIT_IDS.length) + 2}: ${encounter.lineup}. Win: ${trainer.rematchPrize} plus crew drip. Ready?`
    : trainer.challenge;
}

export function trainerAt(x: number, y: number): TrainerDef | undefined {
  return Object.values(TRAINERS).find((t) => t.x === x && t.y === y);
}
